<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Under_the_Hood/Editor_Interfaces';
    let nextLink= '/Documentation/Terminology';
    
</script>
<PrevNextSection {prevLink} {nextLink} />
<SectionComponent tag="h1" 
  id="the-fre-tool-interfaces-1"
  bind:intersecting={visibleById["the-fre-tool-interfaces-1"]}
  onIntersectChange={(visible) => setVisible("the-fre-tool-interfaces-1", visible)}
>The Fre-Tool Interfaces</SectionComponent>
<p>There are two series of interfaces that make Freon and its generated code extensible and flexible.<br>
The first series are the interfaces implemented by the generated code.</p>
<SectionComponent tag="h2" 
  id="freenvironment-2"
  bind:intersecting={visibleById["freenvironment-2"]}
  onIntersectChange={(visible) => setVisible("freenvironment-2", visible)}
>FreEnvironment</SectionComponent>
<p>An object that implements <code>FreEnvironment</code> holds information about where to find all parts
of the language environment. It is usually a singleton object.</p>
<pre class="language-ts"><code class="language-ts">/* File: core/src/environment/FreEnvironment.ts */

export type FreEnvironment = &#123;
	/**
	 * Creates a new model, an implementation of the language defined in the .ast file
	 * @param modelName
	 */
	newModel(modelName: string): FreModel;

	scoper: FreCompositeScoper;
	typer: FreCompositeTyper;
	validator: FreValidator;
	editor: FreEditor;
	writer: FreWriter;
	reader: FreReader;
	interpreter: FreInterpreter;
	projectionHandler: FreProjectionHandler;

	languageName: string;
	fileExtensions: Map&lt;string, string&gt;;
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="frescoper-3"
  bind:intersecting={visibleById["frescoper-3"]}
  onIntersectChange={(visible) => setVisible("frescoper-3", visible)}
>FreScoper</SectionComponent>
<p>An object that implements <code>FreScoper</code> can return the named elements that are visible
for a certain AST node. This node can be a “normal” model element or a namespace.
If the node is a normal element, the names visible in its lexical surroundings are returned.</p>
<p>To implement custom name resolution, write your own <code>FreScoper</code>.</p>
<pre class="language-ts"><code class="language-ts">/* File: core/src/scoper/FreScoper.ts */

export interface FreScoper &#123;
	mainScoper: FreCompositeScoper;

	/**
	 *   Returns all elements that are visible in the namespace containing 'node'. Note that 'node' can
	 *   be any node in the AST, not only namespaces!
	 *
	 *   When parameter 'metaType' is present, it returns all elements that are an instance of 'metaType'.
	 *   There is no default setting for this parameter.
	 *
	 * @param node
	 * @param metaType
	 */
	getVisibleNodes(node: FreNode | FreNodeReference&lt;FreNamedNode&gt;, metaType?: string): FreNamedNode[];

	/**
	 * Returns all nodes and/or node references that represent namespaces which should be added to the namespace
	 * represented by 'node'. Combined with every element is a property called 'recursive', which indicates whether
	 * to include the imported namespaces from imported namespaces.
	 *
	 * @param node
	 */
	importedNamespaces(node: FreNode): FreNamespaceInfo[];

	/**
	 * Returns all nodes and/or node references that represent namespaces which should be used to replace
	 * the parent namespace of the namespace represented by 'node'. Combined with every element is a property
	 * called 'recursive', which indicates whether to include the imported namespaces from alternative namespaces.
	 *
	 * @param node
	 */
	alternativeNamespaces(node: FreNode): FreNamespaceInfo[];
&#125;
</code></pre>
<SectionComponent tag="h2" 
  id="frevalidator-4"
  bind:intersecting={visibleById["frevalidator-4"]}
  onIntersectChange={(visible) => setVisible("frevalidator-4", visible)}
>FreValidator</SectionComponent>
<p>An object that implements <code>FreValidator</code> validates a node in the AST.
Validation may include the node itself or all of its child nodes recursively.</p>
<p>Errors found are returned as a list of <code>FreError</code> objects, each containing a message and a reference
to the faulty node.</p>
<pre class="language-ts"><code class="language-ts">/* File: core/src/validator/FreValidator.ts */

export interface FreValidator &#123;
	/**
	 * Returns a list of errors on 'modelelement' according to the validation rules
	 * stated in the validation definition. If 'includeChildren' is true, the child
	 * nodes of 'modelelement' in the AST are also checked.
	 *
	 * @param modelelement
	 * @param includeChildren
	 */
	validate(modelelement: FreNode, includeChildren?: boolean): FreError[];
&#125;</code></pre>
<h3 id="freerror-1">FreError</h3>
<pre class="language-ts"><code class="language-ts">/* File: core/src/validator/FreValidator.ts */

/**
 * An error consists of a message coupled to the faulty AST node, either a model
 * element or a list of model elements.
 */
export class FreError &#123;
	message: string; // human-readable error message
	reportedOn: FreNode | FreNode[]; // the model element that does not comply
	propertyName: string; // the property of the model element that does not comply, if appropriate
	propertyIndex: number; // the property index of the model element that does not comply, if appropriate
	locationdescription: string; // human-readable indication of 'reportedOn'
	severity: FreErrorSeverity; // indication of how serious the error is, default is 'To Do'

	constructor(
		message: string,
		node: FreNode | FreNode[],
		locationdescription: string,
		propertyName: string,
		severity?: FreErrorSeverity,
		propertyIndex?: number,
	) &#123;
		this.message = message;
		this.reportedOn = node;
		this.locationdescription = locationdescription;
		
		if (typeof severity !== "undefined") &#123;
			this.severity = severity;
		&#125; else &#123;
			this.severity = FreErrorSeverity.ToDo;
		&#125;
		this.propertyName = propertyName;
		this.propertyIndex = propertyIndex;
	&#125;
&#125;

export enum FreErrorSeverity &#123;
	Error = "Error",
	Warning = "Warning",
	Hint = "Hint",
	Improvement = "Improvement",
	ToDo = "TODO",
	Info = "Info",
	NONE = "NONE",
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="fretyper-5"
  bind:intersecting={visibleById["fretyper-5"]}
  onIntersectChange={(visible) => setVisible("fretyper-5", visible)}
>FreTyper</SectionComponent>
<p>An object that implements <code>FreTyper</code> answers type-related questions concerning AST nodes.</p>
<p>To define your own typing system, implement this interface.</p>
<pre class="language-ts"><code class="language-ts">/* File: core/src/typer/FreTyper.ts */

export interface FreTyper &#123;
	// name: string;
	mainTyper: FreTyper;

	/**
	 * Returns true if 'elem' is marked as 'isType' in the Typer definition.
	 * Returns undefined when this typer instance cannot determine the outcome.
	 * @param elem
	 */
	isType(elem: FreNode): boolean | undefined;

	/**
	 * Returns the type of 'modelelement' according to the type rules in the Typer Definition.
	 * Returns undefined when this typer instance cannot determine the outcome.
	 * @param modelelement
	 */
	inferType(modelelement: FreNode): FreType | undefined;

	/**
	 * Returns true if type1 equals type2.
	 * This is a strict equal.
	 * Returns undefined when this typer instance cannot determine the outcome.
	 * @param type1
	 * @param type2
	 */
	equals(type1: FreType, type2: FreType): boolean | undefined;

	/**
	 * Returns true if type1 conforms to type2. The direction is type1 conforms to type2.
	 * Returns undefined when this typer instance cannot determine the outcome.
	 * @param type1
	 * @param type2
	 */
	conforms(type1: FreType, type2: FreType): boolean | undefined;

	/**
	 * Returns true if all types in typelist1 conform to the types in typelist2, in the given order.
	 * Returns undefined when this typer instance cannot determine the outcome.
	 * @param typelist1
	 * @param typelist2
	 */
	conformsList(typelist1: FreType[], typelist2: FreType[]): boolean | undefined;

	/**
	 * Returns the common super type of all types in 'typelist'.
	 * Returns undefined when this typer instance cannot determine the outcome.
	 * @param typelist
	 */
	commonSuper(typelist: FreType[]): FreType | undefined;

	/**
	 * Returns all super types as defined in the typer definition.
	 * Returns undefined when this typer instance cannot determine the outcome.
	 * @param type
	 */
	getSuperTypes(type: FreType): FreType[] | undefined;
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="frereader-6"
  bind:intersecting={visibleById["frereader-6"]}
  onIntersectChange={(visible) => setVisible("frereader-6", visible)}
>FreReader</SectionComponent>
<p>An object that implements <code>FreReader</code> can read a textual representation of a model or AST node.</p>
<p>To create your own parser or file reader, implement this interface.</p>
<pre class="language-ts"><code class="language-ts">/* File: core/src/reader/FreReader.ts */

export interface FreReader &#123;
	/**
	 * Parses and performs a syntax analysis on 'sentence', using the parser and analyser
	 * for 'metatype', if available. If 'sentence' is correct, a model unit will be created,
	 * otherwise an error wil be thrown containing the parse or analysis error.
	 * @param input         the input string which will be parsed
	 * @param metatype      the type of the unit to be created
	 * @param model         the model to which the unit will be added
	 * @param sourceName    the (optional) name of the source that contains 'sentence'
	 */
	readFromString(input: string, metatype: string, model: FreModel, sourceName?: string): FreNode;
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="frewriter-7"
  bind:intersecting={visibleById["frewriter-7"]}
  onIntersectChange={(visible) => setVisible("frewriter-7", visible)}
>FreWriter</SectionComponent>
<p>An object that implements <code>FreWriter</code> can generate a textual representation of an AST node.</p>
<p>To implement your own unparser or writer, define this interface.</p>
<pre class="language-ts"><code class="language-ts">/* File: core/src/writer/FreWriter.ts */

export interface FreWriter &#123;
	/**
	 * Returns a string representation of 'node'.
	 * If 'short' is present and true, then a single-line result will be given.
	 * Otherwise, the result is always a multi-line string.
	 * Note that the single-line-string cannot be parsed into a correct model.
	 *
	 * @param node
	 * @param startIndent
	 * @param short
	 */
	writeToString(node: FreNode, startIndent?: number, short?: boolean): string;

	/**
	 * Returns a string representation of 'node', divided into an array of strings,
	 * each of which contain a single line (without newline).
	 * If 'short' is present and true, then a single-line result will be given.
	 * Otherwise, the result is always a multi-line string.
	 *
	 * @param node
	 * @param startIndent
	 * @param short
	 */
	writeToLines(node: FreNode, startIndent?: number, short?: boolean): string[];

	/**
	 * Returns the name of 'node' if it has one, else returns
	 * a short unparsing of 'node'.
	 * Used by the validator to produce readable error messages.
	 *
	 * @param node
	 */
	writeNameOnly(node: FreNode | undefined): string;
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="frestdlib-8"
  bind:intersecting={visibleById["frestdlib-8"]}
  onIntersectChange={(visible) => setVisible("frestdlib-8", visible)}
>FreStdlib</SectionComponent>
<p>An object that implements <code>FreStdlib</code> holds a number of predefined AST nodes that can be
referred to in user models.</p>
<pre class="language-ts"><code class="language-ts">/* File: core/src/stdlib/FreStdlib.ts  */

export interface FreStdlib &#123;
    elements: FreNamedNode[];

    /**
     * Returns the element named 'name', if it can be found in this library.
     * When 'metatype' is provided, the element is only returned when it is
     * an instance of this metatype.
     */
    find(name: string, metatype?: string): FreNamedNode;
&#125;</code></pre>
<p>The second series of interfaces define parts of the<br>
<a href="/Documentation/Under_the_Hood/Editor_Framework">Freon Editor Framework</a>.</p>


<PrevNextSection {prevLink} {nextLink} />